import { useState } from "react";
import { useDataAdapter } from "@shared-core/data/DataAdapterContext";
import type { CampaignPlan } from "@shared-core/data/api-types";
import {
  NO_TIERS_YET_NOTE,
  STEP_CHANNEL_LABELS,
  tierChoiceHelp,
} from "@shared-core/domain/direct-mail-campaigns";
import { Button } from "../../app/components/primitives/button";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "../../app/components/primitives/dialog";
import { Input } from "../../app/components/primitives/input";
import { Label } from "../../app/components/primitives/label";
import {
  RadioGroup,
  RadioGroupItem,
} from "../../app/components/primitives/radio-group";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../../app/components/primitives/select";
import { failureLogFields, readCampaignActionError } from "./actionErrors";
import { TierCheckboxes } from "./StepTiersEditor";

/** Emails are added with "Add tranche" (or a tranche's "Add reminder"), as
 *  on every campaign, so "Add step" offers the other two kinds. */
type PlanStepChannel = "direct_mail" | "call";
const CHANNEL_OPTIONS: PlanStepChannel[] = ["direct_mail", "call"];

/** The backend's limit on a mailing's or call round's name. */
const MAX_STEP_NAME_LENGTH = 120;

/**
 * "Add step" on a campaign that shows its plan: a Mailing or a Call round,
 * going to the tiers ticked or (a call round) following up an earlier
 * mailing or send. A mailing with no tiers ticked has no recipients until
 * some are chosen in its panel; it never defaults to everyone. Post and
 * phone steps always respect consent, so there is no consent choice here.
 */
export function AddPlanStepDialog({
  campaignId,
  plan,
  open,
  onOpenChange,
  onAdded,
}: {
  campaignId: string;
  plan: CampaignPlan;
  open: boolean;
  onOpenChange: (open: boolean) => void;
  onAdded: () => void;
}) {
  const dataAdapter = useDataAdapter();
  const [channel, setChannel] = useState<PlanStepChannel>("direct_mail");
  const [name, setName] = useState("");
  const [date, setDate] = useState("");
  const [who, setWho] = useState<"tiers" | "follow_up">("tiers");
  const [tierIds, setTierIds] = useState<string[]>([]);
  const [followsStepId, setFollowsStepId] = useState<string>("");
  const [saving, setSaving] = useState(false);
  const [fieldErrors, setFieldErrors] = useState<Record<string, string>>({});
  const [error, setError] = useState<string | null>(null);

  // A mailing always goes to tiers: it is the step others follow up.
  const followUpAllowed = channel !== "direct_mail";
  const isFollowUp = followUpAllowed && who === "follow_up";
  const followableSteps = plan.steps.filter(
    (s) =>
      !s.follows_step_id &&
      (s.channel === "direct_mail" || s.channel === "email"),
  );

  function close(nextOpen: boolean) {
    if (!nextOpen) {
      setChannel("direct_mail");
      setName("");
      setDate("");
      setWho("tiers");
      setTierIds([]);
      setFollowsStepId("");
      setFieldErrors({});
      setError(null);
    }
    onOpenChange(nextOpen);
  }

  function checkForm(): Record<string, string> {
    const errors: Record<string, string> = {};
    if (!name.trim()) errors.name = "Give the step a name.";
    if (!date) errors.scheduled_send_date = "Choose the step's date.";
    if (isFollowUp && !followsStepId) {
      errors.follows_step_id = "Choose which mailing or send this follows up.";
    }
    // A mailing may be added before its tiers are decided; it simply has
    // no recipients until they are. A call round raises a ticket per
    // person, so it needs to know who from the start.
    if (!isFollowUp && channel === "call" && tierIds.length === 0) {
      errors.target_tier_ids = "Choose at least one tier.";
    }
    return errors;
  }

  async function handleAdd() {
    const errors = checkForm();
    setFieldErrors(errors);
    if (Object.keys(errors).length > 0) {
      setError(
        `${Object.keys(errors).length} thing${Object.keys(errors).length === 1 ? "" : "s"} to fix before adding.`,
      );
      return;
    }
    setSaving(true);
    setError(null);
    try {
      await dataAdapter.addMarketingCampaignStep(campaignId, {
        channel,
        name: name.trim(),
        scheduled_send_date: date,
        ...(isFollowUp
          ? { follows_step_id: followsStepId }
          : { target_tier_ids: tierIds }),
      });
      onAdded();
      close(false);
    } catch (e) {
      console.error(
        "marketing.campaign.add_plan_step.failed",
        failureLogFields(e, { campaign_id: campaignId, channel }),
      );
      const read = readCampaignActionError(
        e,
        "Couldn't add the step. Please try again.",
      );
      setFieldErrors(read.fields);
      setError(read.summary);
    } finally {
      setSaving(false);
    }
  }

  function fieldError(field: string) {
    const message = fieldErrors[field];
    return message ? (
      <p
        id={`plan-step-${field}-error`}
        className="text-sm text-red-700 dark:text-red-300"
      >
        {message}
      </p>
    ) : null;
  }

  function describedBy(field: string) {
    return fieldErrors[field]
      ? { "aria-invalid": true, "aria-describedby": `plan-step-${field}-error` }
      : {};
  }

  return (
    <Dialog open={open} onOpenChange={close}>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>Add step</DialogTitle>
          <DialogDescription>
            A mailing goes only to the tiers you choose. A call round goes to
            tiers, or follows up an earlier mailing or send and goes to the
            people it reached. It starts as a Draft. For another email, use "Add
            tranche": emails go to everyone in the audience unless you choose
            tiers in the send's panel.
          </DialogDescription>
        </DialogHeader>
        <div className="space-y-4 py-2">
          <div className="space-y-1.5">
            <Label htmlFor="plan-step-channel">Type</Label>
            <Select
              value={channel}
              onValueChange={(v) => setChannel(v as PlanStepChannel)}
            >
              <SelectTrigger id="plan-step-channel">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                {CHANNEL_OPTIONS.map((c) => (
                  <SelectItem key={c} value={c}>
                    {STEP_CHANNEL_LABELS[c]}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
          <div className="space-y-1.5">
            <Label htmlFor="plan-step-name">Name</Label>
            <Input
              id="plan-step-name"
              value={name}
              maxLength={MAX_STEP_NAME_LENGTH}
              onChange={(e) => setName(e.target.value)}
              placeholder={
                channel === "direct_mail"
                  ? "e.g. Mailing 3 – New Year pack"
                  : "e.g. Call round – follow-up to Mailing 3"
              }
              {...describedBy("name")}
            />
            {fieldError("name")}
          </div>
          <div className="space-y-1.5 max-w-48">
            <Label htmlFor="plan-step-date">Date</Label>
            <Input
              id="plan-step-date"
              type="date"
              value={date}
              onChange={(e) => setDate(e.target.value)}
              {...describedBy("scheduled_send_date")}
            />
            {fieldError("scheduled_send_date")}
          </div>
          <div className="space-y-2">
            <Label>Who it goes to</Label>
            {followUpAllowed && (
              <RadioGroup
                value={who}
                onValueChange={(v) => setWho(v as "tiers" | "follow_up")}
              >
                <div className="flex items-center gap-2">
                  <RadioGroupItem value="tiers" id="plan-step-who-tiers" />
                  <Label htmlFor="plan-step-who-tiers" className="font-normal">
                    Tiers
                  </Label>
                </div>
                <div className="flex items-center gap-2">
                  <RadioGroupItem
                    value="follow_up"
                    id="plan-step-who-follow-up"
                  />
                  <Label
                    htmlFor="plan-step-who-follow-up"
                    className="font-normal"
                  >
                    Follow-up to an earlier mailing or send
                  </Label>
                </div>
              </RadioGroup>
            )}
            {isFollowUp ? (
              <>
                <Select value={followsStepId} onValueChange={setFollowsStepId}>
                  <SelectTrigger
                    aria-label="Mailing or send to follow up"
                    {...describedBy("follows_step_id")}
                  >
                    <SelectValue placeholder="Choose a mailing or send" />
                  </SelectTrigger>
                  <SelectContent>
                    {followableSteps.map((s) => (
                      <SelectItem key={s.id} value={s.id}>
                        {s.name ?? s.label}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
                {fieldError("follows_step_id")}
              </>
            ) : plan.tiers.length === 0 ? (
              <p className="text-sm text-muted-foreground">
                {NO_TIERS_YET_NOTE}
              </p>
            ) : (
              <>
                <TierCheckboxes
                  idPrefix="plan-step-tier"
                  tiers={plan.tiers}
                  selected={tierIds}
                  onChange={setTierIds}
                  invalidMessageId={
                    fieldErrors.target_tier_ids
                      ? "plan-step-target_tier_ids-error"
                      : undefined
                  }
                />
                {fieldError("target_tier_ids")}
                {channel === "direct_mail" && (
                  <p
                    className={
                      tierIds.length === 0
                        ? "text-sm text-amber-800 dark:text-amber-300"
                        : "text-xs text-muted-foreground"
                    }
                  >
                    {tierIds.length === 0
                      ? "No recipients yet – choose which tiers this mailing goes to. You can also choose them later in the mailing's panel."
                      : tierChoiceHelp("direct_mail")}
                  </p>
                )}
              </>
            )}
          </div>
          {error && (
            <p className="text-sm text-red-700 dark:text-red-300" role="alert">
              {error}
            </p>
          )}
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={() => close(false)}>
            Cancel
          </Button>
          <Button onClick={handleAdd} disabled={saving}>
            {saving ? "Adding…" : "Add step"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
